import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import useNavigate from "src/hooks/useNavigate";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { TrialInstitutionMonitoringDocumentsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import * as MonitoringDocumentActions from "../../../actions/monitoringDocument";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { InlineLoading } from "src/components/common";
import { useParams } from "react-router";
import { MonitoringSubMenu } from "../../../helpers/subMenuHelper";

import "./MonitoringDocumentsPage.scss";

interface MonitoringDocumentsPageProps {
    monitoringDocuments: Dtos.MonitoringDocument[];
    loadingMonitoringDocuments: boolean;
    loadMonitoringDocumentsSuccess: boolean;
    loadMonitoringDocumentsFailure: boolean;
    loadMonitoringDocumentsErrorCode: string;
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    lookups: Dtos.Lookup[];
    clearMonitoringDocuments: MonitoringDocumentActions.IMonitoringDocumentClearActionCreator;
    loadMonitoringDocuments: MonitoringDocumentActions.IMonitoringDocumentsLoadByMonitoringIdActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const MonitoringDocumentsPage: FC<MonitoringDocumentsPageProps> = ({
    monitoringDocuments,
    loadingMonitoringDocuments,
    loadMonitoringDocumentsSuccess,
    loadMonitoringDocumentsFailure,
    loadMonitoringDocumentsErrorCode,
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    pageSize,
    lookups,
    clearMonitoringDocuments,
    loadMonitoringDocuments,
    clearTrialInstitution,
    loadTrialInstitution,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10); 
    const monitoringIdNumber = parseInt(monitoringId, 10); 
    
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showMonitoringDocuments = 
        !loadingMonitoringDocuments && 
        loadMonitoringDocumentsSuccess &&
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess;

    // Load Monitoring Documents
    useEffect(() => {
        if (!initalised) {
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber
                )
            );

            loadMonitoringDocuments(monitoringIdNumber);

            setInitalised(true);
        }
    }, [
        initalised,
        trialIdNumber,
        trialInstitutionIdNumber,
        monitoringIdNumber
    ]);

    // Load Trial Institution
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution
    ]);

    // Update drawer items & breadcrumbs
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(MonitoringSubMenu(trialIdNumber, trialInstitutionIdNumber, monitoringIdNumber, permissions));
            setBreadcrumbs(
                TrialInstitutionMonitoringDocumentsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber, 
        trialInstitutionIdNumber, 
        monitoringIdNumber,
        trialInstitution, 
        permissions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringDocumentsFailure) {
            navigate(`/error/${loadMonitoringDocumentsErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`)
        }
    }, [
        loadMonitoringDocumentsFailure, 
        loadMonitoringDocumentsErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Cleanup effect to clear the monitoring documents & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringDocuments();
            clearTrialInstitution();
        };
    }, [clearMonitoringDocuments, clearTrialInstitution]);

    const renderTable = useMemo(() => {
            if (monitoringDocuments?.length == 0) {
                return (
                    <div className="card card-shadow p-3">
                        <div className="alert alert-info d-flex mb-0">
                            <div className="mr-2">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                    fixedWidth
                                />
                            </div>
                            <span>There are currently no documents to view.</span>
                        </div>
                    </div>
                );
            }
    
            const showPagination = monitoringDocuments?.length > pageSize;
    
            return (
                <div className="table-wrapper">
                    <ReactTable
                        data={monitoringDocuments}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        defaultPageSize={showPagination ? pageSize : monitoringDocuments?.length}
                        pageSize={showPagination ? pageSize : monitoringDocuments?.length}
                        showPagination={showPagination}
                        className="-striped -highlight -clickable"
                        noDataText="There are currently no documents to view"
                        resizable={false}
                        onPageSizeChange={(val) => setPageSize(val)}
                        getTdProps={(
                            _state,
                            rowInfo: RowInfo,
                            _column,
                            _instance
                        ) => {
                            return {
                                onClick: (_e, handleOriginal) => {
                                    navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/documents/${rowInfo.original.id}/details`);
    
                                    if (handleOriginal) {
                                        handleOriginal();
                                    }
                                },
                            };
                        }}
                        defaultSorted={[
                            {
                                id: "name",
                                desc: false,
                            },
                        ]}
                        columns={[
                            {
                                id: "id",
                                Header: " ",
                                accessor: "id",
                                maxWidth: 50,
                                Cell: (_props) => (
                                    <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.FILE} fixedWidth />
                                ),
                            },
                            {
                                id: "name",
                                Header: "Name",
                                accessor: "name",
                            },
                        ]}
                    />
                </div>
            );
        }, [
            monitoringDocuments,
            pageSize,
            PAGE_SIZE_OPTIONS,
            setPageSize,
            lookups
        ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Documents</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.MonitoringDocumentCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/documents/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showMonitoringDocuments ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showMonitoringDocuments ? (
                    <div className="card">
                        <div className="patient-tables">{renderTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showMonitoringDocuments,
        renderTable,
        trialIdNumber,
        trialInstitutionIdNumber,
        lookups
    ]);

    return (
        <RestrictedLayout
            loading={loadingMonitoringDocuments || loadingTrialInstitution}
            permission={Dtos.Permission.MonitoringDocumentView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringDocumentsPageProps> => {
    let monitoringDocuments: Dtos.MonitoringDocument[] | undefined =
        state.monitoringDocument.formData instanceof Array
            ? state.monitoringDocument.formData
            : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined =
        !(state.trialInstitution.formData instanceof Array)
            ? state.trialInstitution.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        monitoringDocuments: monitoringDocuments!,
        loadingMonitoringDocuments:
            state.monitoringDocument.loadState &&
            state.monitoringDocument.loadState.status == RequestState.Pending,
        loadMonitoringDocumentsSuccess:
            state.monitoringDocument.loadState &&
            state.monitoringDocument.loadState.status == RequestState.Success,
        loadMonitoringDocumentsFailure:
            state.monitoringDocument.loadState &&
            state.monitoringDocument.loadState.status == RequestState.Failure,
        loadMonitoringDocumentsErrorCode: state.monitoringDocument.loadState.errorCode,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        lookups: state.monitoring.lookups,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringDocumentsPageProps> => {
    return {
        loadMonitoringDocuments: bindActionCreators(MonitoringDocumentActions.LoadMonitoringDocumentsByMonitoringId, dispatch),
        clearMonitoringDocuments: bindActionCreators(MonitoringDocumentActions.Clear, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringDocumentsPage);
